import React, { useMemo, useState, useCallback } from 'react'
import { Calendar, Panel, Popover, Whisper, Tooltip, SelectPicker, Form, Button, CheckPicker } from 'rsuite'
import { CourierWorkShift } from '@@partner:types:app'
import { useFetchAllCourierWorkShifts } from '@@partner:graphql/query/courier/allCourierWorkShifts.gql'
import { useFetchAllShops } from '@@partner:graphql/query/shop/allShops.gql'
import { WORKSHIFT } from '@hive/status'
import { makeBetweenFilter, makeFilter } from '@@partner:helpers/crud/filters'
import SelectCourierPicker from '@components/common/commonFormInput/SelectCourierPicker'
import type { GQL_ID } from '@@partner:types:graphql'
import './ShiftsCalendar.scss'
import { useLazyFetchShops } from '@@partner:graphql/query/shop/shops.gql'
import { renderLazyPickerMenu } from '@components/common/pickers'
import { formatWorkshiftStatusLabel } from '@@partner:helpers/formatters/workshift'

export interface ShiftsCalendarProps {
  onDateClick?: (date: Date) => void
  onEditShift?: (shift: CourierWorkShift) => void
  onViewShift?: (shift: CourierWorkShift) => void // Новый пропс для просмотра
}

export default function ShiftsCalendar ({
  onDateClick,
  onEditShift,
  onViewShift
}: ShiftsCalendarProps) {
  // Состояние для отслеживания текущего месяца календаря
  const [ currentDate, setCurrentDate ] = useState(new Date())
  
  // Состояние фильтров календаря (без дат)
  const [ calendarFilterState, setCalendarFilterState ] = useState<{
    courierIds: GQL_ID[]
    shopIds: GQL_ID[]
  }>({
    courierIds: [],
    shopIds: []
  })

  // Загружаем магазины для фильтра
  const [ fetchShops, { data: shopsData, loading: loadingShopsData } ] = useLazyFetchShops()

  const shopOptions = useMemo(() => {
    if (!shopsData?.shops) return []
    
    return shopsData.shops.map((shop: any) => ({
      label: shop.name,
      value: shop.id
    }))
  }, [ shopsData ])

  // Вычисляем диапазон дат для календаря (обычно календарь показывает 6 недель)
  const calendarDateRange = useMemo(() => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    
    // Первый день месяца
    const firstDayOfMonth = new Date(year, month, 1)
    
    // Находим первый понедельник (или воскресенье, зависит от локали)
    const firstDayOfWeek = firstDayOfMonth.getDay()
    const daysToSubtract = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1 // Понедельник как первый день
    const calendarStartDate = new Date(firstDayOfMonth)
    calendarStartDate.setDate(firstDayOfMonth.getDate() - daysToSubtract)
    
    // Последний день месяца
    const lastDayOfMonth = new Date(year, month + 1, 0)
    
    // Находим последнее воскресенье
    const lastDayOfWeek = lastDayOfMonth.getDay()
    const daysToAdd = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek
    const calendarEndDate = new Date(lastDayOfMonth)
    calendarEndDate.setDate(lastDayOfMonth.getDate() + daysToAdd)
    
    return {
      startDate: calendarStartDate,
      endDate: calendarEndDate
    }
  }, [ currentDate ])

  // Создаем фильтр для календаря на основе видимых дат и пользовательских фильтров
  const calendarFilters = useMemo(() => {
    const { startDate, endDate } = calendarDateRange
    
    // Устанавливаем время начала дня для startDate и конца дня для endDate
    const startTimestamp = new Date(startDate)
    startTimestamp.setHours(0, 0, 0, 0)
    
    const endTimestamp = new Date(endDate)
    endTimestamp.setHours(23, 59, 59, 999)
    
    const filters = []
    
    // Фильтр по датам (обязательный)
    filters.push(
      makeBetweenFilter('ts_starting', {
        from: startTimestamp.getTime(),
        to: endTimestamp.getTime()
      })
    )
    
    // Фильтр по курьеру (если выбран)
    if (calendarFilterState.courierIds.length > 0) {
      filters.push(makeFilter('courier_id', 'contains', calendarFilterState.courierIds))
    }
    
    // Фильтр по магазину (если выбран)
    if (calendarFilterState.shopIds.length > 0) {
      filters.push(makeFilter('shop_id', 'contains', calendarFilterState.shopIds))
    }
    
    return {
      and: filters
    }
  }, [ calendarDateRange, calendarFilterState ])

  // Собственный GraphQL запрос для календаря - загружаем больше данных без пагинации
  const { courierWorkshifts, loading, error } = useFetchAllCourierWorkShifts({
    filter: calendarFilters, // Применяем фильтры календаря
    pager: { page: 1, limit: 1000 }, // Большой лимит для календаря
    sort: {
      direction: 0,
      fieldName: 'ts_starting'
    }
  })

  const data = useMemo(() => courierWorkshifts || [], [ courierWorkshifts ])

  // Группируем смены по датам
  const shiftsByDate = useMemo(() => {
    const groups: Record<string, CourierWorkShift[]> = {}

    data.forEach(shift => {
      if (shift.plannedStartAt) {
        const date = new Date(shift.plannedStartAt)
        
        // Используем локальную дату вместо UTC для избежания проблем с временными зонами
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        const dateKey = `${year}-${month}-${day}` // YYYY-MM-DD в локальной временной зоне
        
        if (!groups[dateKey]) {
          groups[dateKey] = []
        }
        groups[dateKey].push(shift)
      }
    })
    
    return groups
  }, [ data ])

  // Генерация цвета для курьера на основе его ID
  const getCourierColor = (courierId: string | number): string => {
    const colors = [
      '#1976d2', '#d32f2f', '#388e3c', '#f57c00', 
      '#7b1fa2', '#5d4037', '#c2185b', '#00796b',
      '#303f9f', '#689f38', '#e64a19', '#455a64'
    ]
    const index = (typeof courierId === 'string' ? parseInt(courierId, 10) : courierId) % colors.length
    return colors[index]
  }

  // Форматирование времени смены
  const formatShiftTime = (plannedStart: number, plannedFinish: number) => {
    const startDate = new Date(plannedStart)
    const finishDate = new Date(plannedFinish)
    
    const startTime = `${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')}`
    const finishTime = `${finishDate.getHours().toString().padStart(2, '0')}:${finishDate.getMinutes().toString().padStart(2, '0')}`
    
    return `${startTime}-${finishTime}`
  }

  // Рендер ячейки календаря
  const renderCell = (date: Date) => {
    // Используем тот же способ формирования ключа даты, что и при группировке
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const dateKey = `${year}-${month}-${day}`
    
    const shiftsForDate = shiftsByDate[dateKey] || []
    
    if (shiftsForDate.length === 0) {
      return null
    }

    const displayedShifts = shiftsForDate.slice(0, 6) // Увеличиваем с 4 до 6 смен
    const hasMore = shiftsForDate.length > 6

    return (
      <div className="shifts-calendar-cell">
        {displayedShifts.map((shift, index) => {
          const statusConfig = formatWorkshiftStatusLabel(shift.status)
          const timeText = formatShiftTime(Number(shift.plannedStartAt), Number(shift.plannedFinishAt))
          const courierName = shift.courier ? `${shift.courier.first_name} ${shift.courier.last_name}` : 'Не назначен'
          
          // Получаем название магазина из массива магазинов
          let shopName = 'Без магазина'
          if (shift.courier?.shops && shift.courier.shops.length > 0) {
            // Показываем только название первого магазина для компактности
            shopName = shift.courier.shops[0].name
          }
          
          const courierColor = shift.courier ? getCourierColor(shift.courier.id) : '#666666'
          
          const shiftTooltip = (
            <div>
              <div><strong>{courierName}</strong></div>
              <div>Магазин: {shopName}</div>
              <div>Время: {timeText}</div>
              <div>Статус: {statusConfig?.label || `Неизвестно (${shift.status})`}</div>
              {shift.vehicle && (
                <div>Транспорт: {shift.vehicle.brand} {shift.vehicle.model}</div>
              )}
              {shift.courier?.shops && shift.courier.shops.length > 1 && (
                <div>
                  <strong>Все магазины:</strong>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {shift.courier.shops.map(shop => (
                      <li key={shop.id}>{shop.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )

          return (
            <Whisper
              key={shift.id}
              trigger="hover"
              speaker={<Popover>{shiftTooltip}</Popover>}
            >
              <div
                className="shift-badge-wrapper"
                onClick={(e) => {
                  e.stopPropagation()
                  // Используем onViewShift для просмотра деталей, если доступно
                  if (onViewShift) {
                    onViewShift(shift)
                  }
                  else {
                    onEditShift?.(shift)
                  }
                }}
                style={{ backgroundColor: courierColor }}
              >
                <div className="shift-badge-content">
                  <div className="first-line">
                    <div className="courier-name">{courierName}</div>
                    <div className="shift-time">{timeText}</div>
                  </div>
                  <div className="shop-name">
                    {shopName}
                    {shift.courier?.shops && shift.courier.shops.length > 1 && (
                      <span className="multiple-shops-indicator"> •{shift.courier.shops.length}</span>
                    )}
                  </div>
                </div>
                {/* Индикатор статуса справа */}
                <div 
                  className="status-indicator"
                  style={{ 
                    backgroundColor: statusConfig?.color === 'blue' ? '#1976d2' :
                      statusConfig?.color === 'green' ? '#388e3c' :
                        statusConfig?.color === 'yellow' ? '#f57c00' :
                          statusConfig?.color === 'orange' ? '#ff9800' :
                            statusConfig?.color === 'red' ? '#d32f2f' : '#666666'
                  }}
                  title={statusConfig?.label || `Неизвестно (${shift.status})`}
                />
              </div>
            </Whisper>
          )
        })}
        {hasMore && (
          <div className="more-badge">
            +{shiftsForDate.length - 6}
          </div>
        )}
      </div>
    )
  }

  const handleDateSelect = (date: Date) => {
    onDateClick?.(date)
  }

  // Обработчик изменения месяца в календаре
  const handleCalendarChange = (date: Date) => {
    setCurrentDate(date)
  }

  // Обработчики фильтров
  const handleCourierFilterChange = useCallback((courierIds: GQL_ID[]) => {
    setCalendarFilterState(prev => ({ ...prev, courierIds: courierIds || [] }))
  }, [])

  const handleShopFilterChange = useCallback((shopIds: GQL_ID[]) => {
    setCalendarFilterState(prev => ({ ...prev, shopIds: shopIds || [] }))
  }, [])

  const handleResetFilters = useCallback(() => {
    setCalendarFilterState({
      courierIds: [],
      shopIds: []
    })
  }, [])

  if (error) {
    return (
      <Panel header="Календарь смен" bordered>
        <div className="error-message">
          Ошибка загрузки календаря: {error.message}
        </div>
      </Panel>
    )
  }

  return (
    <Panel header={
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Календарь смен</span>
        {loading && (
          <span style={{ 
            fontSize: '12px', 
            color: '#999', 
            fontWeight: 'normal'
          }}>
            Обновление...
          </span>
        )}
      </div>
    } bordered className="shifts-calendar" style={{ position: 'relative' }}>
      {/* Фильтры календаря */}
      <div className="calendar-filters" style={{ 
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: '#495057' }}>Фильтры:</span>
          <SelectCourierPicker
            isMultiple={true}
            value={calendarFilterState.courierIds}
            onChange={handleCourierFilterChange}
          />


          <CheckPicker
            value={calendarFilterState.shopIds}
            data={shopOptions}
            onOpen={fetchShops}
            searchable={false}
            placeholder="Магазины"
            valueKey="value"
            labelKey="label"
            size="sm"
            style={{ maxWidth: 260 }}
            renderMenu={renderLazyPickerMenu(loadingShopsData, shopsData)}
            onChange={(value) => handleShopFilterChange(value || [])}
          />
        </div>

        {(calendarFilterState.courierIds.length > 0 || calendarFilterState.shopIds.length > 0) && (
          <Button
            onClick={handleResetFilters}
            size="sm"
            appearance="subtle"
            color="red"
            startIcon={<span>×</span>}
          >
            Сбросить
          </Button>
        )}
      </div>

      {loading && data.length === 0 && (
        <div className="loading-message" style={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          zIndex: 10,
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '10px 20px',
          borderRadius: '4px',
          border: '1px solid #ddd'
        }}>
          Загрузка календаря...
        </div>
      )}
      <Calendar
        locale={{
          sunday: 'Вс',
          monday: 'Пн', 
          tuesday: 'Вт',
          wednesday: 'Ср',
          thursday: 'Чт',
          friday: 'Пт',
          saturday: 'Сб',
          ok: 'ОК',
          today: 'Сегодня',
          yesterday: 'Вчера'
        }}
        renderCell={renderCell}
        onSelect={handleDateSelect}
        onChange={handleCalendarChange}
        isoWeek
      />
      
      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-info">
            <strong>Цветовое кодирование:</strong> Каждый курьер имеет свой уникальный цвет
          </div>
        </div>
        <div className="legend-item">
          <div className="legend-status-info">
            <strong>Статусы смен:</strong>
            <span className="status-indicators">
              <span className="status-indicator" style={{ backgroundColor: '#1976d2' }}>Запланирована</span>
              <span className="status-indicator" style={{ backgroundColor: '#388e3c' }}>Начата</span>
              <span className="status-indicator" style={{ backgroundColor: '#f57c00' }}>Завершена</span>
              <span className="status-indicator" style={{ backgroundColor: '#d32f2f' }}>Отменена</span>
            </span>
          </div>
        </div>
      </div>
    </Panel>
  )
}